import {Typography, makeStyles} from '@material-ui/core';
import {Link, useLocation, useHistory} from 'react-router-dom';
import React, {useState, useMemo} from 'react';
import {useDebounce} from '../../../utils/react';
import SearchInput from '../../../elements/SearchInput';
import FunnelItem from './components/FunnelItem/FunnelItem';
import FunnelInit from './components/FunnelInit/FunnelInit';
import {colors} from '../../../assets/styles/colors';
import {
  useMultichatFunnels,
  useInitializeCurrentFunnelDetails,
  useCurrentMultichatFunnel,
} from './utils';
import {useIsFeatureInTier} from '../../../utils/component';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {DragDropContext, Droppable} from 'react-beautiful-dnd';
import {useCurrentMultichatFunnelActions} from './utils';
import {
  getMultichatFunnelService,
  addConversationToFunnelService,
} from './promises';
import {useAlert} from '../../../elements/Alert/AlertContext';

const useStyles = makeStyles(theme => ({
  root: {
    height: 'calc(100% - 100px)',
    width: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: 14,
    color: colors.grey700,
    marginBlock: '.5rem',
  },
  tierRestriction: {
    color: colors.grey700,
    fontSize: 14,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 'calc(70vh - 100px)',
    maxWidth: '500px',
    flexDirection: 'column',
    margin: '0 auto',
    gap: '16px',
  },
  tierRestrictionMessage: {
    fontSize: 16,
    color: colors.grey700,
    textAlign: 'center',
  },
  funnelSelector: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${colors.grey300}`,
  },
  funnelCard: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: `1px solid ${colors.grey300}`,
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '140px',
    textAlign: 'center',
    fontWeight: 500,
    fontSize: '14px',
    color: colors.grey700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: colors.grey100,
    },
  },
  funnelCardActive: {
    border: `2px solid ${colors.primary}`,
    backgroundColor: `${colors.primary}10`,
    color: colors.primary,
    '&:hover': {
      backgroundColor: `${colors.primary}20`,
    },
  },
  funnelCardDraggingOver: {
    backgroundColor: `${colors.primary}20`,
    border: `2px dashed ${colors.primary}`,
  },
  funnelCardDragging: {
    border: `2px dashed ${colors.primary}50`,
    backgroundColor: `${colors.primary}05`,
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    maxWidth: '400px',
  },
  searchInput: {
    backgroundColor: '#ffffff !important',
    border: `1px solid ${colors.grey300}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: colors.grey400,
    },
    '&.Mui-focused': {
      borderColor: colors.primary,
      boxShadow: `0 0 0 2px ${colors.primary}20`,
    },
  },
}));

const MAX_FUNNELS = 3;

const Funnels = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const {showAlert} = useAlert();

  const {funnels} = useMultichatFunnels();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [
    location.search,
  ]);

  const currentFunnelId = useMemo(() => {
    const funnelIdParam = searchParams.get('funnelId');
    if (funnelIdParam && funnels?.some(f => f.id === funnelIdParam)) {
      return funnelIdParam;
    }
    return funnels?.[0]?.id || null;
  }, [searchParams, funnels]);

  const showNewFunnelForm = searchParams.get('new') === 'true';

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const currentFunnel = useCurrentMultichatFunnel();

  const {
    dragAndDropConversations,
    reorderFunnelLanes,
    removeConversationFromLane,
  } = useCurrentMultichatFunnelActions();

  useInitializeCurrentFunnelDetails(currentFunnelId, debouncedSearch);

  const [draggingType, setDraggingType] = useState(null);

  const hasFunnelFeature = useIsFeatureInTier('FUNNEL');

  if (!hasFunnelFeature) {
    return (
      <div className={classes.tierRestriction}>
        <Typography className={classes.tierRestrictionMessage}>
          Tu plan actual no incluye la funcionalidad de embudos de ventas. Si
          deseas acceder a esta funcionalidad, por favor actualiza tu plan.
        </Typography>
        <CustomButton to="/tiers-list" component={Link} variant="contained">
          Ver Planes
        </CustomButton>
      </div>
    );
  }

  if (!funnels) {
    return <div className={classes.root}>LOADING...</div>;
  }

  const handleFunnelSelect = funnelId => {
    history.push(`${location.pathname}?funnelId=${funnelId}`);
  };

  const handleNewFunnel = () => {
    history.push(`${location.pathname}?new=true`);
  };

  function onDragStart(initial) {
    setDraggingType(initial.type);
  }

  async function onDragEnd(result) {
    setDraggingType(null);
    const {source, destination, draggableId, type} = result;

    if (!destination) {
      return;
    }

    // Moving a column
    if (type === 'COLUMN') {
      if (currentFunnel && currentFunnel.lanes) {
        const newLanesIds = Array.from(currentFunnel.lanes.map(l => l.id));
        newLanesIds.splice(source.index, 1);
        newLanesIds.splice(destination.index, 0, draggableId);
        reorderFunnelLanes(newLanesIds);
      }
      return;
    }

    // Dropped on a different funnel tab
    if (destination.droppableId.startsWith('funnel-tab-')) {
      const targetFunnelId = destination.droppableId.replace('funnel-tab-', '');
      if (targetFunnelId === currentFunnelId) return; // dropped on same tab

      try {
        // Find target funnel's main lane or first lane
        const targetFunnelData = await getMultichatFunnelService(
          targetFunnelId
        );
        const mainLane =
          targetFunnelData.lanes.find(l => l.setting) ||
          targetFunnelData.lanes[0];

        if (!mainLane) {
          showAlert('El funnel destino no tiene listas', {type: 'error'});
          return;
        }

        // Add to target funnel
        await addConversationToFunnelService(targetFunnelId, mainLane.id, [
          draggableId,
        ]);

        // Remove from current funnel
        if (removeConversationFromLane) {
          removeConversationFromLane(source.droppableId, draggableId);
        }

        showAlert('Conversación movida exitosamente', {type: 'success'});
      } catch (error) {
        console.error(error);
        showAlert('Error al mover la conversación', {type: 'error'});
      }
      return;
    }

    // Normal conversation drop within the same funnel
    if (
      destination.droppableId !== source.droppableId ||
      destination.index !== source.index
    ) {
      dragAndDropConversations({
        destination: {
          index: destination.index,
          laneId: destination.droppableId,
        },
        source: {
          index: source.index,
          laneId: source.droppableId,
          conversationId: draggableId,
        },
      });
    }
  }

  return (
    <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
      <div className={classes.root}>
        <div>
          <Typography className={classes.title}>
            Organiza tareas visualmente en columnas, como "pendiente," "en
            proceso," y "completado." Mueve las tarjetas entre las columnas para
            gestionar eficientemente tu funnel del ventas, priorizando las
            conversaciones necesarias y cerrando procesos de manera rápida.
          </Typography>
        </div>

        {funnels.length > 0 && (
          <div className={classes.funnelSelector}>
            {funnels.map(funnel => (
              <Droppable
                key={funnel.id}
                droppableId={`funnel-tab-${funnel.id}`}
                isDropDisabled={draggingType === 'COLUMN'}
              >
                {(provided, snapshot) => {
                  const isDraggingOver = snapshot.isDraggingOver;
                  const canReceiveDrop =
                    draggingType === 'DEFAULT' && funnel.id !== currentFunnelId;

                  let cardClassName = classes.funnelCard;

                  if (funnel.id === currentFunnelId) {
                    cardClassName += ` ${classes.funnelCardActive}`;
                  }

                  if (isDraggingOver) {
                    cardClassName += ` ${classes.funnelCardDraggingOver}`;
                  } else if (canReceiveDrop) {
                    cardClassName += ` ${classes.funnelCardDragging}`;
                  }

                  return (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={cardClassName}
                      onClick={() => handleFunnelSelect(funnel.id)}
                    >
                      {funnel.name}
                      <div style={{display: 'none'}}>
                        {provided.placeholder}
                      </div>
                    </div>
                  );
                }}
              </Droppable>
            ))}
            {funnels.length < MAX_FUNNELS && (
              <div className={classes.funnelCard} onClick={handleNewFunnel}>
                + Nuevo Funnel
              </div>
            )}
          </div>
        )}

        {funnels.length === 0 || showNewFunnelForm ? (
          <FunnelInit />
        ) : (
          <>
            <div className={classes.searchContainer}>
              <SearchInput
                placeholder="Buscar por nombre o teléfono"
                value={search}
                onChange={setSearch}
                className={classes.searchInput}
                fullWidth
              />
            </div>
            <FunnelItem draggingType={draggingType} search={debouncedSearch} />
          </>
        )}
      </div>
    </DragDropContext>
  );
};

export default Funnels;
